import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { SectionHeader, CTABanner } from "@/components/Shared";
import { Search, LayoutGrid, UtilityPole, Route, Trees, Mountain, TrendingUp } from "lucide-react";
import { IMAGES } from "@/data/content";

const PILLARS = [
  { icon: Search, title: "Land Opportunity Identification", description: "We identify land parcels in locations with genuine growth potential, connectivity and context." },
  { icon: LayoutGrid, title: "Layout Planning", description: "Thoughtful layouts that balance plot sizes, open spaces, circulation and future usability." },
  { icon: UtilityPole, title: "Infrastructure", description: "Planning for essential infrastructure — internal roads, drainage, water and power provisions." },
  { icon: Route, title: "Accessibility", description: "Clear access and approach roads are central to how we evaluate and plan every layout." },
  { icon: Trees, title: "Landscaping", description: "Green surroundings and considered landscaping that make developments pleasant to live in." },
  { icon: Mountain, title: "Surroundings", description: "Pleasant natural and neighbourhood surroundings that make a layout worth belonging to." },
  { icon: TrendingUp, title: "Long-Term Value", description: "Locations and plans chosen with appreciation, usability and end-user demand in mind." },
];

const PlottedDevelopment = () => (
  <>
    <SEO
      title="Plotted Land Development | Evaya Ventures"
      description="Evaya Ventures' plotted development approach focuses on thoughtfully planned land parcels, access, infrastructure and surroundings to create attractive opportunities for end users and investors."
      path="/plotted-development"
      breadcrumbs={[{ name: "Home", path: "/" }, { name: "Plotted Development", path: "/plotted-development" }]}
    />

    <PageHero
      overline="Plotted Land Development"
      title="Creating Better-Planned Land Opportunities"
      statement="From Earth to Extraordinary Opportunities."
      description="Evaya Ventures focuses on thoughtfully planned plotted land opportunities, with attention to location, accessibility, infrastructure, surroundings and long-term value."
      image={IMAGES.landField}
      imageAlt={IMAGES.landFieldAlt}
      testId="plotted-hero"
    />

    {/* Intro */}
    <section className="py-24 lg:py-32" data-testid="plotted-intro">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-6">
          <SectionHeader
            overline="Our Thinking"
            title="Land, planned the way it deserves to be"
            testId="plotted-thinking"
          />
          <Reveal delay={0.1}>
            <p className="mt-6 text-base leading-relaxed text-ink/65 md:text-lg">
              A well-planned plotted development is more than divided land. It is access that works, infrastructure that is considered, green surroundings that endure and a layout that holds its value for decades.
            </p>
            <p className="mt-5 text-base leading-relaxed text-ink/65">
              Evaya Ventures is currently evaluating land opportunities for plotted developments that serve both end users looking to build their future and investors seeking well-chosen land assets.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="lg:col-span-6">
          <div className="grid grid-cols-2 gap-4">
            <img src={IMAGES.plottedCard} alt={IMAGES.plottedCardAlt} loading="lazy" className="h-64 w-full object-cover md:h-80" />
            <img src={IMAGES.landAerial} alt={IMAGES.landAerialAlt} loading="lazy" className="mt-8 h-64 w-full object-cover md:h-80" />
          </div>
        </Reveal>
      </div>
    </section>

    {/* Pillars */}
    <section className="border-t border-ink/10 bg-sand py-24 lg:py-32" data-testid="plotted-pillars">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <SectionHeader
          overline="What We Focus On"
          title="The anatomy of a well-planned plotted development"
          testId="plotted-pillars"
        />
        <div className="mt-14 grid grid-cols-1 gap-px border border-ink/10 bg-ink/10 md:grid-cols-2 lg:grid-cols-3">
          {PILLARS.map((pillar, i) => (
            <div key={pillar.title} className="bg-white">
              <Reveal delay={i * 0.06} className="h-full p-8 lg:p-10">
                <pillar.icon className="h-7 w-7 text-brand-dark" strokeWidth={1.5} aria-hidden="true" />
                <h3 className="mt-5 font-display text-2xl text-ink">{pillar.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-ink/60">{pillar.description}</p>
              </Reveal>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* For buyers & investors */}
    <section className="py-24 lg:py-32" data-testid="plotted-audiences">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <SectionHeader
          overline="Who It Serves"
          title="Opportunities for end users and investors alike"
          testId="plotted-audiences"
        />
        <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-2 lg:gap-10">
          <Reveal>
            <article data-testid="plotted-endusers-card" className="h-full border border-ink/10 bg-[#FAFAFA] p-8 lg:p-12">
              <h3 className="font-display text-3xl text-ink">For End Users</h3>
              <p className="mt-4 text-base leading-relaxed text-ink/65">
                Thoughtfully planned plots in well-chosen locations give families the freedom to build homes on their own terms — with access, infrastructure and surroundings already considered.
              </p>
            </article>
          </Reveal>
          <Reveal delay={0.1}>
            <article data-testid="plotted-investors-card" className="h-full border border-ink/10 bg-[#FAFAFA] p-8 lg:p-12">
              <h3 className="font-display text-3xl text-ink">For Investors</h3>
              <p className="mt-4 text-base leading-relaxed text-ink/65">
                Land remains one of the most enduring asset classes. Our focus on location, planning and infrastructure is designed to create plotted opportunities with genuine long-term value.
              </p>
            </article>
          </Reveal>
        </div>
      </div>
    </section>

    <CTABanner title="Interested in plotted development opportunities?" copy="Register your interest and we will reach out as our plotted development opportunities are announced." />
  </>
);

export default PlottedDevelopment;
